import { onAuthorizationContextChange } from "./authorizationContext.js";
/**
 * Analytics Query API client.
 *
 * Talks to the graph-traversal analytics service exposed via grpc-gateway:
 *   POST /v1/analytics:execute   — ExecuteQuery
 *   GET  /v1/analytics/schema    — DescribeSchema
 *
 * Local dev brings the stack up via:
 *   - shared-analytics::analytics-test-server  (gRPC, :50080, no auth)
 *   - grpc-gateway-api  (HTTP/JSON bridge, :8081)
 *
 * Used by individual handlers in apiClient.js as they're migrated off
 * the dashboard's bespoke /api routes. Each handler builds a Query spec
 * out of the helpers here, calls runQuery, and shapes the rows back to
 * what existing FE consumers expect.
 */

// Auth-aware fetch lives in restClient.js (the lower-level HTTP
// layer); analytics calls go through the same wrapper as REST calls
// so bearer attachment, peer-refresh check, and 401 retry are all
// in one place.
import { authedFetch } from "./restClient.js";
import { isPageUnloading, track } from "./analytics/client.js";
import { EVENT_NAME, QUERY_ERROR_KIND } from "./analytics/surfaceVocabulary.js";
import { getDimAdapter } from "./dimAdapters.js";
import { ensureQueryJoins } from "./queryLangJoins.js";
import { getPinnedGenerationId, getGenerationRevision } from "./generationPin.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { denialCopy, parseDenial } from "./denialReason.js";
import { namespaceForCommand } from "./signalCatalog.js";
import { getAuthContextEpoch, getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// Thrown when ExecuteQuery is denied by RBAC (HTTP 403). Carries the
// structured, pre-parsed reason so the explore surface can render
// role-aware copy — naming the missing capability or the sensitive read
// a denied column requires — instead of surfacing a raw `ExecuteQuery
// 403: {...}` string. Every role holds analytics execute.raw, so a row-
// level query is never refused for its shape; a denial here is a missing
// permission or a sensitive-column read the role lacks.
export class AnalyticsDenialError extends Error {
    constructor(rawBody, status) {
        super(`ExecuteQuery ${status}: ${rawBody}`);
        this.name = "AnalyticsDenialError";
        this.status = status;
        this.parsed = parseDenial(rawBody);
    }

    get reason() { return this.parsed.reason; }
}

export class AnalyticsInfrastructureError extends Error {
    constructor(message, { status = null, cause } = {}) {
        super(message);
        this.name = "AnalyticsInfrastructureError";
        this.status = status;
        this.analyticsInfrastructure = true;
        if (cause !== undefined) this.cause = cause;
    }
}

// Client/spec failures are not infrastructure failures, but callers that can
// safely degrade across a rolling schema deployment still need the HTTP status
// and response body to identify one specific unsupported field.
export class AnalyticsQueryError extends Error {
    constructor(message, { status, responseText = "" } = {}) {
        super(message);
        this.name = "AnalyticsQueryError";
        this.status = status;
        this.responseText = responseText;
    }
}

// User-facing copy for an analytics error: role-aware text for an RBAC
// denial, or a neutral message otherwise. Pass the caller's permissions
// posture for the role clause.
export function analyticsErrorCopy(err, caps) {
    if (err instanceof AnalyticsDenialError) return denialCopy(err.parsed, caps);
    return denialCopy(err, caps);
}

// ── DescribeSchema (memoized) ──────────────────────────────────────

let _schemaPromise = null;
let _schemaGeneration = 0;
let _schemaReady = false;
onAuthIdentityChange(invalidateAnalyticsSchema);
export function invalidateAnalyticsSchema() { _schemaPromise = null; _schemaReady = false; _schemaGeneration++; }
onAuthorizationContextChange(invalidateAnalyticsSchema);
export function describeSchema({ refresh = false } = {}) {
    if (refresh && _schemaReady) invalidateAnalyticsSchema();
    if (_schemaPromise) return _schemaPromise;
    const epoch = getAuthIdentityEpoch();
    const generation = _schemaGeneration;
    const request = authedFetch("/v1/analytics/schema")
        .then(async (r) => {
            if (!r.ok) {
                const text = await r.text().catch(() => "");
                if (_schemaPromise === request) _schemaPromise = null;
                const err = new Error(`DescribeSchema ${r.status}: ${text || r.statusText}`);
                // Carry the status so callers can distinguish an RBAC denial
                // (403 — reachable + authed, role lacks tenant.analytics.execute)
                // from a real backend failure. The readiness probe relies on this.
                err.status = r.status;
                throw err;
            }
            const schema = await r.json();
            if (getAuthIdentityEpoch() !== epoch || generation !== _schemaGeneration) {
                const err = new Error("Authentication identity changed while describing analytics schema");
                err.name = "AuthContextChangedError";
                throw err;
            }
            _schemaReady = true;
            return schema;
        }).catch((error) => { if (_schemaPromise === request) _schemaPromise = null; throw error; });
    _schemaPromise = request;
    return request;
}

export function _resetSchemaForTests() {
    invalidateAnalyticsSchema();
}

// ── ExecuteQuery ───────────────────────────────────────────────────

// `spec` is the Query message (no outer { query: ... } wrapper).
// Returns { columns, rows, cost, resolvedSpec }.
// Accepts a stringified spec as well — LLM tool calls have been
// observed to emit the spec as a JSON-encoded string when the tool
// schema is unconstrained, which sent a quoted blob to the gRPC
// gateway and produced "proto: syntax error (line 1:10)" failures.
//
// Inflight dedup: identical specs sent concurrently share a single
// network round trip. Different code paths routinely emit the same
// COUNT_DISTINCT body — e.g. DimensionPane fires getDimensionCount
// while ScopeLevel's batched getDimensionCounts and
// executeDimensionEntries' totalSpec all reach the same analytics-api
// query — and the higher-level query() dedup keys on RPC method, so
// it misses the cross-source case. Coalescing at the wire boundary
// catches all of them; the key is the full body the server would
// see, so any spec divergence (extraDims, filter shape) correctly
// forks into separate requests.
const _runQueryInflight = new Map();

export const MAX_ANALYTICS_REQUESTS_IN_FLIGHT = 6;
let _requestsInFlight = 0;
const _requestWaiters = [];

async function withRequestSlot(run) {
    if (_requestsInFlight < MAX_ANALYTICS_REQUESTS_IN_FLIGHT) {
        _requestsInFlight += 1;
    } else {
        await new Promise((resolve) => _requestWaiters.push(resolve));
    }
    try {
        return await run();
    } finally {
        const next = _requestWaiters.shift();
        if (next) next();
        else _requestsInFlight -= 1;
    }
}

// Result cache — the SEQUENTIAL half of dedup. `_runQueryInflight` collapses
// specs that are in flight at the same instant; this collapses specs that
// repeat *after* the first has settled. The Explore fanouts (Anatomy above
// all) fire the same org-total / bundle / baseline spec from several hooks
// across successive render commits, so most duplicates land just after the
// first resolves — they miss the inflight window and, without this, each one
// re-hits ClickHouse. Keyed by the exact serialized body (generation pin
// already injected) and the refresh revision, so an unpinned refresh cannot
// reuse a previous default's response. Old entries age out within the cap.
const _resultCache = new Map();
const RESULT_CACHE_TTL_MS = 30_000;
const RESULT_CACHE_MAX = 500;

// Fresh read with an LRU touch so hot specs survive eviction. Returns
// `undefined` on miss/expiry (a real result is always an object).
function readFreshResult(key, staleMs) {
    const hit = _resultCache.get(key);
    if (!hit) return undefined;
    if (Date.now() - hit.time > staleMs) {
        _resultCache.delete(key);
        return undefined;
    }
    _resultCache.delete(key);
    _resultCache.set(key, hit);
    return hit.result;
}

function rememberResult(key, result) {
    _resultCache.set(key, { result, time: Date.now() });
    // Evict oldest-by-insertion; readFreshResult re-inserts on hit, so the
    // front of the map is genuinely the least-recently-used entry.
    while (_resultCache.size > RESULT_CACHE_MAX) {
        _resultCache.delete(_resultCache.keys().next().value);
    }
}

// Explore generation pin (GenerationPicker). The planner
// pins every ILO cluster-entity read to the latest completed
// clustering generation by default; an explicit predicate on the
// entity's generation_id field drops that pin and reads the named
// generation instead (O-1547, PR #1197). So the dashboard pin is
// expressed per-step: every ILO step in the spec gets a stepFilter
// `generation_id = <pin>` EQ pred. stepFilter (ON semantics) rather
// than the top-level WHERE so LEFT label joins keep their
// keep-parent-rows behavior, and root-entity specs (the cluster tree
// fetch) pin through root.step_filter, which the planner honors the
// same way.
//
// ENTITY_TYPE_ILO_CLUSTER_GENERATION is deliberately ABSENT from this
// map: it's the discovery surface (list all generations to pick one),
// and a generation_id predicate on it would both collapse the listing
// to the pinned row and drop its own default filter.
//
// Steps that already constrain their generation_id — an explicit
// per-query override, or a resolvedSpec replay that carries a prior
// injection — win over the pin. Applied before the dedup key is
// computed, so pinned and unpinned forms of the same query fork into
// separate requests.
const GENERATION_PIN_FIELD_BY_ENTITY = {
    ENTITY_TYPE_ILO_EVENT_CLUSTER: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_GENERATION_ID",
    ENTITY_TYPE_ILO_CLUSTER:       "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_ID",
};

// Any pred on (alias, field) anywhere under `node` — same any-reference
// semantics the planner's rebuild_pin_suppressed uses.
function filterReferencesField(node, alias, field) {
    if (node == null || typeof node !== "object") return false;
    const f = node.pred?.field;
    if (f?.stepAlias === alias && f?.field === field) return true;
    return (Array.isArray(node) ? node : Object.values(node))
        .some((v) => filterReferencesField(v, alias, field));
}

// All EntityStep nodes in a spec: the root plus every join target.
function collectEntitySteps(root) {
    const steps = [];
    (function walk(step) {
        if (!step || typeof step !== "object") return;
        if (step.entity && step.alias) steps.push(step);
        for (const child of step.children ?? []) walk(child?.target);
    })(root);
    return steps;
}

function withGenerationPin(spec) {
    if (!spec || typeof spec !== "object" || Array.isArray(spec) || !spec.root) return spec;
    const pinned = getPinnedGenerationId();
    if (!pinned) return spec;
    const needsPin = (step, topFilter) => {
        const field = GENERATION_PIN_FIELD_BY_ENTITY[step.entity];
        if (!field) return false;
        if ((step.stepFilter ?? []).some((f) => filterReferencesField(f, step.alias, field))) return false;
        return !filterReferencesField(topFilter ?? null, step.alias, field);
    };
    if (!collectEntitySteps(spec.root).some((s) => needsPin(s, spec.filter))) return spec;
    const clone = structuredClone(spec);
    for (const step of collectEntitySteps(clone.root)) {
        if (!needsPin(step, clone.filter)) continue;
        step.stepFilter = [
            ...(step.stepFilter ?? []),
            {
                pred: {
                    field: { stepAlias: step.alias, field: GENERATION_PIN_FIELD_BY_ENTITY[step.entity] },
                    op: "OPERATOR_EQ",
                    value: pinned,
                },
            },
        ];
    }
    return clone;
}

// `optional` on an EntityStep is the dashboard's own annotation: apiClient's
// runDegradableQuery drops a decorative join the viewer's row scope cannot
// reach. The analytics API declares no such field, and the gateway parses this
// route strictly, so one leaked marker fails the whole query with
// `unknown field "optional"`. Strip it at the wire boundary so a builder that
// sends through runQuery directly cannot put it there (OR-333).
function withoutStepMarkers(spec) {
    if (!spec || typeof spec !== "object" || Array.isArray(spec) || !spec.root) return spec;
    if (!stepTreeHasMarker(spec.root)) return spec;
    return { ...spec, root: stripStepMarkers(spec.root) };
}

function stepTreeHasMarker(step) {
    if (!step || typeof step !== "object") return false;
    if ("optional" in step) return true;
    return (step.children || []).some((child) => stepTreeHasMarker(child?.target));
}

function stripStepMarkers(step) {
    if (!step || typeof step !== "object") return step;
    const { optional: _optional, ...rest } = step;
    if (!Array.isArray(rest.children)) return rest;
    return {
        ...rest,
        children: rest.children.map((child) =>
            child && typeof child === "object" ? { ...child, target: stripStepMarkers(child.target) } : child),
    };
}

async function rawExecute(body, signal) {
    let res;
    try {
        res = await authedFetch("/v1/analytics:execute", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: body }),
            signal,
        });
    } catch (error) {
        if (error?.name === "AbortError" || signal?.aborted) throw error;
        throw new AnalyticsInfrastructureError(
            error instanceof Error ? error.message : String(error),
            { cause: error },
        );
    }
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        const message = `ExecuteQuery ${res.status}: ${text || res.statusText}`;
        // 403 is an RBAC denial — surface it typed + parsed so the UI can
        // render role-aware copy and offer the aggregate path. Transport,
        // throttling, and server failures carry a bounded infrastructure
        // type; client/spec failures remain generic Errors.
        if (res.status === 403) throw new AnalyticsDenialError(text || res.statusText, res.status);
        if (res.status === 408 || res.status === 429 || res.status >= 500) {
            throw new AnalyticsInfrastructureError(message, { status: res.status });
        }
        throw new AnalyticsQueryError(message, { status: res.status, responseText: text });
    }
    try {
        return await res.json();
    } catch (error) {
        if (error?.name === "AbortError" || signal?.aborted) throw error;
        throw new AnalyticsInfrastructureError(
            error instanceof Error ? error.message : String(error),
            { status: res.status, cause: error },
        );
    }
}

// A field ref that lost its proto field — a synthetic dim routed into a
// graph query, or a builder that spread a null dimFieldRef into a spec —
// serializes as enum 0 on the wire, and the server rejects the whole query
// as `field Unspecified`. Worse than the failure itself, panels on
// stale-while-revalidate then retry the identical broken spec forever.
// Reject it here at the dashboard's JSON boundary,
// so the mistake surfaces as one immediate, descriptive client-side error.
// Checks every `field` key: a ref holder ({ field: <FieldRef> }) must carry
// an object with a truthy inner `field`, and a leaf `field` (the enum name
// inside a FieldRef) must be a non-empty value.
export function assertFieldRefsResolved(node, path = "spec") {
    if (Array.isArray(node)) {
        node.forEach((v, i) => assertFieldRefsResolved(v, `${path}[${i}]`));
        return;
    }
    if (!node || typeof node !== "object") return;
    if ("field" in node) {
        const f = node.field;
        const unresolved = f == null || f === ""
            || (typeof f === "object" && !Array.isArray(f) && !f.field);
        if (unresolved) {
            const step = (f && typeof f === "object" ? f.stepAlias : node.stepAlias) ?? "?";
            throw new Error(
                `analytics spec has an unresolved field ref at ${path} (step ${step}) — `
                + "synthetic/unsupported dims have no graph field; route them to their bespoke pipeline "
                + "instead of a graph query",
            );
        }
    }
    for (const [k, v] of Object.entries(node)) {
        if (v && typeof v === "object") assertFieldRefsResolved(v, `${path}.${k}`);
    }
}

function abortError(signal) {
    if (signal?.reason instanceof Error) return signal.reason;
    const error = new Error("analytics query aborted");
    error.name = "AbortError";
    return error;
}

function subscribeToInflight(entry, signal) {
    entry.subscribers += 1;
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (fn, value) => {
            if (settled) return;
            settled = true;
            if (signal) signal.removeEventListener("abort", onAbort);
            entry.subscribers = Math.max(0, entry.subscribers - 1);
            fn(value);
        };
        const onAbort = () => {
            finish(reject, abortError(signal));
            // Identical query callers share one request. Abort the wire only
            // after every subscriber has detached; one stopped canvas cell
            // must not cancel a still-visible consumer of the same spec.
            if (!entry.settled && entry.subscribers === 0) {
                if (_runQueryInflight.get(entry.key) === entry) {
                    _runQueryInflight.delete(entry.key);
                }
                entry.controller.abort(abortError(signal));
            }
        };
        if (signal) {
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener("abort", onAbort, { once: true });
        }
        entry.promise.then(
            (result) => finish(resolve, result),
            (error) => finish(reject, error),
        );
    });
}

// `opts.cache` (default true) opts a caller into the result cache — reads a
// fresh entry when present, writes on success. Pass `{ cache: false }` for
// reads that must always re-execute (kernel / chat-tool runs); those still get
// inflight dedup, just never a stored or served result. `opts.staleMs` overrides
// the freshness window for this call.
export async function runQuery(spec, { cache = true, staleMs = RESULT_CACHE_TTL_MS, signal = null, requestContext = null } = {}) {
    if (signal?.aborted) throw abortError(signal);
    const authContextEpoch = getAuthContextEpoch();
    const parsed = typeof spec === "string" ? safeJsonParse(spec, spec) : spec;
    // Predicate values remain logical values on this JSON boundary. The
    // backend encodes String parameters for ClickHouse's HTTP grammar; doing
    // that here as well changes LIKE escapes and literal backslashes.
    let body = parsed;
    // The generation pin lives in the URL (`gen` hash param), readable
    // synchronously at load, so the first queries on a cold load already see
    // it. withGenerationPin injects `generation_id = <pin>` as a stepFilter on
    // each ILO step; cluster scope predicates (cluster_subtree) live in that
    // SAME step's stepFilter, so the planner resolves the subtree walk in the
    // pinned generation (O-1623, O-1624). Cluster-subtree scoping is fully
    // server-side now — no client-side leaf expansion or 1024-cap chunking.
    body = withGenerationPin(body);
    body = withoutStepMarkers(body);
    assertFieldRefsResolved(body);
    // Cache and in-flight identities include a non-secret auth generation.
    // Without it, a mounted tab that adopts a different session over the auth
    // BroadcastChannel can receive rows cached under the previous tenant for
    // an otherwise identical spec.
    const key = JSON.stringify({ authContextEpoch, generationRevision: getGenerationRevision(), requestContext, query: body });
    // Sequential dedup: a fresh cached result satisfies repeat specs that
    // arrive after the first settled — the stragglers the inflight map misses.
    if (cache) {
        const cached = readFreshResult(key, staleMs);
        if (cached !== undefined) return cached;
    }
    // Concurrent dedup: identical specs in flight right now share one round trip.
    const existing = _runQueryInflight.get(key);
    if (existing) {
        if (cache) existing.cacheRequested = true;
        return subscribeToInflight(existing, signal);
    }
    const controller = new AbortController();
    const entry = {
        key,
        controller,
        subscribers: 0,
        settled: false,
        cacheRequested: cache,
        promise: null,
    };
    const startedAt = Date.now();
    const promise = withRequestSlot(() => rawExecute(body, controller.signal)).then((result) => {
        // A session may change while ClickHouse is still answering. Never hand
        // that previous session's response to a caller running in the new auth
        // context, even though it was not served from the result cache.
        if (getAuthContextEpoch() !== authContextEpoch) {
            const error = new Error("analytics auth context changed during request");
            error.name = "AuthContextChangedError";
            throw error;
        }
        return result;
    }, (error) => {
        reportQueryFailure(body, error, Date.now() - startedAt);
        throw error;
    });
    entry.promise = promise;
    _runQueryInflight.set(key, entry);
    // Clear the inflight entry once the request settles. Use then(cb, cb)
    // rather than .finally(cb): .finally re-raises any rejection on the
    // derived promise it returns, and nothing awaits that derived promise —
    // so a failing query logs a spurious "Uncaught (in promise)" even when the
    // actual caller .catch()es the returned `promise`. A rejection handler here
    // resolves the cleanup branch; the returned `promise` still rejects for the
    // caller. Only successful results are remembered — a failed query must never
    // pin an error into the cache.
    const clearInflight = () => {
        entry.settled = true;
        if (_runQueryInflight.get(key) === entry) _runQueryInflight.delete(key);
    };
    promise.then(
        (result) => { if (entry.cacheRequested) rememberResult(key, result); clearInflight(); },
        clearInflight,
    );
    return subscribeToInflight(entry, signal);
}

// Client-observed query failures for product analytics. Slow successes are
// deliberately not reported here: the server records them with the plan in
// its own slow-query table. Only the failure class and the query shape leave
// the browser, never predicate values.
// rawExecute wraps transport errors in AnalyticsInfrastructureError with the
// original as `cause`; the cause is what carries the class worth reporting.
function rootError(error, depth = 0) {
    return error?.cause instanceof Error && depth < 3 ? rootError(error.cause, depth + 1) : error;
}

export function queryErrorKind(error) {
    const root = rootError(error);
    const name = root?.name;
    // AbortError from fetch, KernelRunCancelledError from a torn-down canvas,
    // and any other cancellation are the person moving on, not failures.
    if (name === "AbortError" || /Cancel|Abort/.test(String(name ?? ""))) return QUERY_ERROR_KIND.ABORT;
    if (name === "AuthMissingError") return QUERY_ERROR_KIND.AUTH;
    // fetch rejects with a TypeError for every transport failure, including a
    // body read cut off by navigation after a 200, so the status is moot.
    if (name === "TypeError") return QUERY_ERROR_KIND.NETWORK;
    if (name === "TimeoutError") return QUERY_ERROR_KIND.TIMEOUT;
    if (name === "AnalyticsDenialError") return QUERY_ERROR_KIND.DENIED;
    if (name === "AuthContextChangedError") return QUERY_ERROR_KIND.AUTH;
    if (name === "AnalyticsInfrastructureError") return typeof error?.status === "number" ? QUERY_ERROR_KIND.SERVER : QUERY_ERROR_KIND.NETWORK;
    if (error?.name === "AnalyticsInfrastructureError") return typeof error?.status === "number" ? QUERY_ERROR_KIND.SERVER : QUERY_ERROR_KIND.NETWORK;
    if (name === "AnalyticsQueryError") return QUERY_ERROR_KIND.QUERY;
    return QUERY_ERROR_KIND.UNKNOWN;
}

// A spec is a tree: `{ root: { entity, children: [{ target: {...} }] } }`.
// The step count is the number of entity nodes in it.
function countSteps(node) {
    if (!node || typeof node !== "object") return 0;
    const children = Array.isArray(node.children) ? node.children : [];
    return 1 + children.reduce((n, child) => n + countSteps(child?.target), 0);
}

function reportQueryFailure(body, error, durationMs) {
    const kind = queryErrorKind(error);
    // An abort is the person navigating or a view unmounting, not a failure,
    // and a transport error while the page is unloading is the same thing.
    if (kind === QUERY_ERROR_KIND.ABORT) return;
    if (kind === QUERY_ERROR_KIND.NETWORK && isPageUnloading()) return;
    const root = body?.root ?? body;
    track(EVENT_NAME.DATA_QUERY_FAILED, {
        error_kind: kind,
        // The error class name is static code vocabulary; it is what turns an
        // "unknown" into a mapping in queryErrorKind.
        error_name: typeof rootError(error)?.name === "string" ? rootError(error).name : null,
        entity: typeof root?.entity === "string" ? root.entity : null,
        steps: countSteps(body?.root) || null,
        duration_ms: Math.round(durationMs),
    });
}

// Convenience: runQuery + flatten RowValues into plain objects keyed by
// the response's `columns` array.
export async function runQueryRows(spec, opts) {
    return rowsToObjects(await runQuery(spec, opts));
}

// Test-only: clear the inflight-dedup map AND the result cache so tests don't
// share a settled promise or a cached result across cases (both keyed by the
// serialized body).
export function _resetQueryInflightForTests() {
    for (const entry of _runQueryInflight.values()) {
        try { entry.controller?.abort(); } catch { /* noop */ }
    }
    _runQueryInflight.clear();
    _resultCache.clear();
    _requestsInFlight = 0;
    _requestWaiters.length = 0;
}

function safeJsonParse(s, fallback) {
    try { return JSON.parse(s); } catch { return fallback; }
}

export function rowsToObjects({ columns = [], rows = [] }) {
    return rows.map(({ values = [] }) => {
        const obj = {};
        for (let i = 0; i < columns.length; i++) obj[columns[i]] = values[i];
        return obj;
    });
}

// ── Time range translation ─────────────────────────────────────────

const DEFAULT_DURATION_SECONDS = String(30 * 86400);

// Dashboard range arrives as either:
//   - null/undefined (no range supplied)              → 30d relative
//   - { from: ISO, to: ISO }                          → absolute
export function rangeToTimeRange(range) {
    if (!range || (!range.from && !range.to)) {
        return { relative: { durationSeconds: DEFAULT_DURATION_SECONDS } };
    }
    return { absolute: { start: range.from, end: range.to } };
}

// Returns { fromMs, toMs } for client-side bucket math. Mirrors the
// resolved time range the planner uses.
export function rangeBoundsMs(range) {
    if (range?.from && range?.to) {
        return { fromMs: Date.parse(range.from), toMs: Date.parse(range.to) };
    }
    const toMs = Date.now();
    return { fromMs: toMs - 30 * 86400 * 1000, toMs };
}

// Bucket size that splits [from, to] into `bucketCount` even buckets.
// Floors at 1s so we never emit `bucket_seconds=0` (the proto rejects).
export function bucketSecondsForRange(range, bucketCount) {
    const { fromMs, toMs } = rangeBoundsMs(range);
    const span = Math.max(0, toMs - fromMs);
    return Math.max(1, Math.floor(span / 1000 / Math.max(1, bucketCount)));
}

// ── Dim-key → analytics field/edge mapping ─────────────────────────

// Dashboard dim keys (singular) → either a direct PROMPT field, or a
// "needs cluster join" marker. Anything not listed here can't be
// expressed as-is against the proto today (artifact dims, project,
// model — all flagged in the earlier audit).
//
// `field` is the DimensionField enum name to use as the predicate /
// group-by target. `viaCluster` means the caller must add the
// PROMPT_HAS_EVENT_CLUSTER → ILO_EVENT_CLUSTER join to the spec
// (handled by `ensureClusterJoin` below).
const DIM_INFO = {
    endpoint: { onStep: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },
    // Directory identity — the directory PERSON who ran a prompt, resolved
    // PROMPT → ENDPOINT_USER_ASSIGNMENT (the junction) → DIRECTORY_USER (the
    // person) (O-2400). `viaAssignment` makes buildPromptRootAndFilter add both
    // join hops; the group-by / scope target is the person's stable
    // directory source_id, so one person's prompts across all their devices
    // collapse into a single identity. Replaces the old derived-rewrite adapter
    // (identityDim.js) — identity now scopes, breaks down, contrasts, and
    // kernel-scopes like any other dimension.
    identity:  { onStep: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_SOURCE_ID", viaAssignment: true },
    // `provider` is the canonical attribution dimension; `agent_type` is the
     // deprecated surface still dual-populated on the backend. Provider values
     // come back as canonical lower-kebab tags (`anthropic`, `openai`, …) —
     // the label loader runs them through deriveAgentIdentity to humanize.
     agent:    { onStep: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER", valueIsLabel: true },
    session:  { onStep: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
    // `field` is the projectable leaf id (group-by / output). `scopeField` is
    // the virtual, filter-only `cluster_subtree`, used when cluster is a SCOPE
    // step: the server walks the clicked node's descendant subtree (O-1623),
    // so a parent scopes to all its leaves without client-side enumeration.
    cluster:  { onStep: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID", scopeField: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE", viaCluster: true },
    // Signals — the durable backend signal output (signal_tags), reached by the
    // PROMPT → SignalTags edge exactly as cluster reaches ILO_EVENT_CLUSTER. The
    // dim value is the tag `namespace` (e.g. `security.credscan`); the adapter's
    // labelFor maps it to the human title from the FE alert registry. `viaSignalTags`
    // makes buildPromptRootAndFilter add the join + put the namespace predicate on
    // the joined `st` step.
    signal:   { onStep: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE", viaSignalTags: true },
    // Model lives on the TOKEN entity (multi-grain `token_observation`
    // store, O-2021 — one row per token observation, grain-resolved
    // server-side). Reached by rooting at ENTITY_TYPE_TOKEN (see
    // buildUsageRootAndFilter); the per-prompt join was removed in O-1805.
    // Value IS the label (model name string), so no separate label
    // entity to join.
    model:    { onStep: "pu", field: "DIMENSION_FIELD_TOKEN_MODEL", viaUsage: true, valueIsLabel: true },
    // Array-typed PROMPT virtual fields. The planner explodes them
    // via arrayJoin on group-by, so a metric of COUNT(event_id)
    // counts (prompt × element) rows — wrong for "how many prompts
    // touched this value." `array: true` flips the metric to
    // COUNT_DISTINCT(event_id) so the count matches dashboard
    // semantics. Filter ops use arrayExists/hasAny element-wise per
    // the proto's Array(String) restrictions.
    tool_call: { onStep: "p", field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES",  array: true, valueIsLabel: true },
    file:      { onStep: "p", field: "DIMENSION_FIELD_PROMPT_RESOURCE_PATHS",   array: true, valueIsLabel: true },
    // Synthetic dim — entries are MCP server ids derived from
    // PROMPT_TOOL_CALL_NAMES via the `mcp__<server>__<tool>` naming
    // convention. The filter builder emits a LIKE predicate on the
    // tool-name array; the planner runs LIKE on Array(String) as
    // arrayExists, so a single predicate = "any tool by this server."
    mcp_server: { onStep: "p", synthetic: true, valueIsLabel: true },
    // Signal-backed dim — a skill is the durable `skill` signal_tags annotation
    // the backend emits per Skill-tool launch / SKILL.md read (O-2586). Reached
    // PROMPT → SignalTags exactly like `signal`/`cluster`, but it's TAG-valued
    // within a FIXED namespace: the dim value is the skill name (st.tag) and
    // `signalNamespace` gates the join to namespace="skill". (Was a synthetic
    // resource_paths %skill% dim; that key space — path slugs — was disjoint from
    // the Skill-tool names the signal carries, so the dim now reads the signal.)
    skill: { onStep: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG", viaSignalTags: true, signalNamespace: "skill", valueIsLabel: true },
    // Signal-backed dim — a memory is the durable `memory` signal_tags annotation
    // the backend emits per memory-file read/write/edit. Reached PROMPT →
    // SignalTags exactly like `signal`/`skill`, TAG-valued within a FIXED
    // namespace: the dim value is the memory slug (st.tag) and `signalNamespace`
    // gates the join to namespace="memory". (Was a synthetic resource_paths
    // `%/memory/<slug>.md%` dim; the dim now reads the signal — see memories.js.)
    memory: { onStep: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG", viaSignalTags: true, signalNamespace: "memory", valueIsLabel: true },
};

export function isAnalyticsSupportedDim(dim) {
    return Object.prototype.hasOwnProperty.call(DIM_INFO, dim);
}

// Array-semantics dims (file paths, tool names) need
// COUNT_DISTINCT(event_id) instead of COUNT() for the activity
// metric — the planner's arrayJoin fan-out double-counts otherwise.
export function dimIsArray(dim) {
    return Boolean(DIM_INFO[dim]?.array);
}

// Value-is-label dims have no separate label entity to join. The id
// is the human label (file path, tool name, agent name).
export function dimValueIsLabel(dim) {
    return Boolean(DIM_INFO[dim]?.valueIsLabel);
}

// Synthetic dims (mcp_server) carry predicate fragments instead of a
// proto field. They need bespoke entry/count pipelines instead of the
// standard group-by + COUNT_DISTINCT path; the adapter owns that pipeline.
export function dimIsSynthetic(dim) {
    return Boolean(DIM_INFO[dim]?.synthetic);
}

// The fixed signal namespace a tag-valued signal-backed dim reads (skill →
// "skill", memory → "memory"). Non-null only for a `viaSignalTags` dim that
// gates on a namespace and matches the dim ids against `st.tag`; null for the
// `signal` dim (namespace-VALUED, no fixed namespace) and every non-signal dim.
// Lets scope-step builders add the PROMPT→SignalTags join generically instead of
// branching per dim (DIM_INFO isn't exported).
export function dimSignalNamespace(dim) {
    const info = DIM_INFO[dim];
    return info?.viaSignalTags ? (info.signalNamespace ?? null) : null;
}

// Returns the list of scope steps using dims this client can't express.
// Caller can short-circuit to legacy if non-empty.
export function unsupportedScopeDims(scopePath) {
    const out = [];
    for (const step of scopePath || []) {
        if (!isAnalyticsSupportedDim(step.dim)) out.push(step.dim);
    }
    return out;
}

// Returns a FieldRef for the given dim key, or null if the dim is
// unsupported OR carries no proto field (synthetic dims like mcp_server
// express themselves as predicate fragments, not a group-by field — a ref
// built for one would serialize as enum 0 / FIELD_UNSPECIFIED on the wire
// and the planner rejects the whole query).
export function dimFieldRef(dim) {
    const info = DIM_INFO[dim];
    if (!info?.field) return null;
    return { stepAlias: info.onStep, field: info.field };
}

// Field ref for grouping/projecting a dim on a PROMPT root (flow links,
// PROMPT-rooted breakdowns). Identical to dimFieldRef EXCEPT for `model`:
// the per-prompt usage edge was removed in O-1805, so the pu-anchored
// DIMENSION_FIELD_TOKEN_MODEL that dimFieldRef returns is unreachable
// from a PROMPT root — referencing an undeclared `pu` step 400s with
// "unknown step alias: pu". On a PROMPT root, model is the prompt-level
// request-body column DIMENSION_FIELD_PROMPT_MODEL: the same field
// buildPromptRootAndFilter maps a model SCOPE onto and the trace list / context
// rows project (== response-resolved model for Claude). A breakdown that
// genuinely needs the response-resolved value must root at ENTITY_TYPE_TOKEN
// instead (the Models-tab path), where dimFieldRef's pu ref is valid.
export function promptRootDimRef(dim) {
    if (dim === "model") return { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" };
    return dimFieldRef(dim);
}

export function dimNeedsClusterJoin(dim) {
    return Boolean(DIM_INFO[dim]?.viaCluster);
}

export function dimNeedsUsageJoin(dim) {
    return Boolean(DIM_INFO[dim]?.viaUsage);
}

export function dimNeedsAssignmentJoin(dim) {
    return Boolean(DIM_INFO[dim]?.viaAssignment);
}

// Dashboard scope paths speak PLURAL dim keys (the sidebar/registry
// form: `endpoints`, `sessions`, …). DIM_INFO and buildPromptRootAndFilter
// speak the SINGULAR analytics form (`endpoint`, `session`, …). This
// maps the analytics-supported plural dims onto their singular DIM_INFO
// key so a raw dashboard scopePath can be fed straight into the
// prompt-root builder (e.g. by the kernel runner, which scopes a kernel
// to wherever the user is on the activity surface).
//
// Dims with no prompt-rooted analytics expression are intentionally
// absent: `files`/`projects`/`hosts`/`commands`/`urls` aren't in DIM_INFO.
// Their steps are returned in `unsupported` so callers can decide to warn /
// fall back rather than silently under-filter. (`identities` IS expressible
// now — O-2400 registered endpoint_user_assignments in the graph, so it
// scopes server-side via the assignment join instead of the old rewrite.)
const DASHBOARD_DIM_TO_API = {
    endpoints: "endpoint",
    identities: "identity",
    agents: "agent",
    sessions: "session",
    clusters: "cluster",
    models: "model",
    tools: "tool_call",
    mcp_servers: "mcp_server",
    skills: "skill",
    memories: "memory",
};

// The dashboard (plural) dims that form a real server-side scope filter.
// This is the set the chat's navigation tool may open as a scope tab —
// a dim outside it would render a tab whose filter silently no-ops.
export function navigableScopeDims() {
    return Object.keys(DASHBOARD_DIM_TO_API);
}

export function isNavigableScopeDim(dim) {
    return Object.prototype.hasOwnProperty.call(DASHBOARD_DIM_TO_API, dim);
}

// scopePath (plural dims) → { apiScope (singular dims, buildPromptRootAndFilter-ready),
// unsupported: [plural dims that have no prompt-rooted expression] }.
export function dashboardScopeToApiScope(scopePath = []) {
    const apiScope = [];
    const unsupported = [];
    for (const step of scopePath || []) {
        if (!step || typeof step !== "object") continue;
        const apiDim = DASHBOARD_DIM_TO_API[step.dim];
        if (!apiDim) { unsupported.push(step.dim); continue; }
        const next = { dim: apiDim };
        if (Array.isArray(step.ids) && step.ids.length) next.ids = step.ids;
        else if (step.id != null) next.id = step.id;
        // Supported dim but no id/ids → can't form a predicate. Treat it
        // like an unsupported dim so the caller fails closed rather than
        // running a scope that looks complete but silently under-filters.
        else { unsupported.push(step.dim); continue; }
        apiScope.push(next);
    }
    return { apiScope, unsupported };
}

// ── PROMPT-rooted spec builder ─────────────────────────────────────

// Build the root EntityStep + child join steps + top-level FilterExpr
// from a dashboard scope path, plus any extra dims (group-by) that need
// cross-step joins (currently just cluster).
//
// Returns { root, filter, unsupported } ready to drop into a Query message.
// `unsupported` lists scope dims this PROMPT root cannot carry. It is empty
// today: `model` (the only dim that used to land here, after O-1805 removed
// the per-prompt usage edge) now scopes on PROMPT's own request-body
// model column, so PROMPT-rooted breakdowns stay real under a model scope. The
// field is retained as a forward guard — a scoped-breakdown caller MUST still
// fail closed if it ever goes non-empty rather than silently under-filter.
//
// Predicates land at the top-level filter (WHERE-tree) rather than
// step_filter (ON-clause) because dashboard scope semantics are "drop
// rows that don't match" — not the LEFT-JOIN-keep-parent-rows-empty
// shape that step_filter implies.
// AND two FilterExprs, flattening a top-level andExpr so the steer doesn't
// nest needlessly. (Local to avoid importing composeFilters from
// queryLangCompile, which would add an analyticsClient→queryLangCompile edge.)
function andFilterExpr(a, b) {
    const parts = [];
    for (const f of [a, b]) {
        if (!f) continue;
        if (f.andExpr?.children) parts.push(...f.andExpr.children);
        else parts.push(f);
    }
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0];
    return { andExpr: { children: parts } };
}

// `steer` is a compiled query ({ filter, joins }) — the sidebar scope steer.
// It's folded into the SAME root + filter the scope produces, so every consumer
// of this builder (analytics counts, entries, the dim adapters, the cluster
// rollup) applies it identically to a scope drill. Its joins dedupe against the
// scope's own via ensureQueryJoins (alias-keyed); its filter ANDs on top.
export function buildPromptRootAndFilter({ scopePath = [], extraDims = [], steer = null } = {}) {
    const root = { alias: "p", entity: "ENTITY_TYPE_PROMPT" };
    let needsCluster = false;
    let needsUsage = false;
    let needsAssignment = false;
    let needsSignalTags = false;
    const directPreds = [];
    const clusterPreds = [];
    const usagePreds = [];
    const signalTagsPreds = [];
    // Scopes this PROMPT-rooted builder CANNOT carry. Empty today: `model` (the
    // lone viaUsage dim) now resolves to a PROMPT.model predicate below, so
    // nothing populates this. Kept as a forward guard — callers still fail
    // closed if it ever goes non-empty (see apiClient.js / kernelScope.js).
    const unsupported = [];

    for (const step of scopePath) {
        const info = DIM_INFO[step.dim];
        if (!info) continue;
        const ids = step.ids?.length ? step.ids : step.id != null ? [step.id] : [];
        if (!ids.length) continue;
        // Synthetic dims (flag, mcp_server, …) carry predicate
        // fragments instead of a field/IN mapping. Their adapters
        // know how to build the right FilterExpr. See
        // src/lib/dimAdapters.js for the registry; each adapter's
        // `predicate(ids, stepAlias)` returns a FilterExpr fragment
        // that AND-composes into the directPreds bucket alongside
        // the standard field-IN predicates.
        if (info.synthetic) {
            const combined = getDimAdapter(step.dim)?.predicate(ids, info.onStep) ?? null;
            if (combined) directPreds.push(combined);
            continue;
        }
        // Cluster scope uses the virtual `cluster_subtree` (scopeField), not
        // `cluster_id`: the server resolves the clicked node's whole descendant
        // subtree (O-1623). Other dims have no scopeField and use `field`.
        const pred = predicate(info.onStep, info.scopeField ?? info.field, ids, step.dim);
        if (info.viaCluster) {
            needsCluster = true;
            clusterPreds.push(pred);
        } else if (info.viaSignalTags) {
            // Scope to prompts whose signal_tags match — the predicate(s) ride the
            // joined SignalTags (`st`) step's stepFilter (INNER = semi-join to the
            // firing prompts), mirroring the cluster path. Two shapes:
            //  • TAG-valued within a fixed namespace (skill): gate the join to
            //    namespace=signalNamespace AND match the dim ids against st.tag.
            //  • NAMESPACE-valued (signal): the dim ids are commands → map them to
            //    backend namespaces and match st.namespace.
            if (info.signalNamespace) {
                needsSignalTags = true;
                signalTagsPreds.push({ pred: { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" }, op: "OPERATOR_EQ", value: info.signalNamespace } });
                signalTagsPreds.push({
                    pred: {
                        field: { stepAlias: info.onStep, field: info.field },
                        op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
                        value: ids.length > 1 ? ids : ids[0],
                    },
                });
            } else {
                const ns = ids.map(namespaceForCommand).filter(Boolean);
                if (ns.length) {
                    needsSignalTags = true;
                    signalTagsPreds.push({
                        pred: {
                            field: { stepAlias: info.onStep, field: info.field },
                            op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
                            value: ns.length > 1 ? ns : ns[0],
                        },
                    });
                }
            }
        } else if (info.viaUsage) {
            // The per-prompt usage edge was removed in O-1805, but the
            // prompt's request-body model is on PROMPT itself
            // (DIMENSION_FIELD_PROMPT_MODEL). For Anthropic that equals the
            // response-resolved TOKEN_MODEL the model picker surfaces
            // (verified on staging: request == response model on every Claude
            // row), so a model SCOPE is a direct PROMPT.model predicate — no
            // token join, no fail-close. This keeps every PROMPT-rooted
            // breakdown (identities / skills / mcp / flags / counts / clusters)
            // and prompt-rooted kernels real under a model scope instead of
            // blanking to 0. (model-as-a-GROUP-BY still roots at the TOKEN
            // entity via the Models-tab path; this is scope-as-filter only.)
            directPreds.push(predicate("p", "DIMENSION_FIELD_PROMPT_MODEL", ids, step.dim));
        } else if (info.viaAssignment) {
            // Directory identity (O-2400): the predicate targets the joined
            // ENDPOINT_USER_ASSIGNMENT step (`u`) — flag the join so it's added
            // below. The join is INNER and the planner injects the entity's
            // is_deleted=0 row filter, so only live assignments match. A prompt
            // on a device shared by N users fans to N rows; grouping/filtering
            // by source_user_id is the intended per-person attribution.
            needsAssignment = true;
            directPreds.push(pred);
        } else {
            directPreds.push(pred);
        }
    }
    if (extraDims.some((d) => DIM_INFO[d]?.viaCluster)) needsCluster = true;
    if (extraDims.some((d) => DIM_INFO[d]?.viaUsage))   needsUsage = true;
    if (extraDims.some((d) => DIM_INFO[d]?.viaAssignment)) needsAssignment = true;
    if (extraDims.some((d) => DIM_INFO[d]?.viaSignalTags)) needsSignalTags = true;

    const children = [];
    if (needsCluster) {
        children.push({
            edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "ec",
                entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
                // Outlier exclusion (cluster_id > 0) PLUS the cluster_subtree
                // scope predicate(s), if any. Both ride in THIS step's
                // stepFilter (ON-clause), co-located with the generation_id pin
                // withGenerationPin injects on the same step — so the planner
                // resolves the subtree walk in the pinned generation
                // (O-1623/O-1624). The ec join is INNER, so ON ≡ WHERE for
                // filtering; the placement is purely about pin co-location.
                stepFilter: [
                    {
                        pred: {
                            field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" },
                            op: "OPERATOR_GT", value: 0,
                        },
                    },
                    ...clusterPreds,
                ],
            },
        });
    }
    if (needsSignalTags) {
        // PROMPT → SignalTags on event_id (O-2437). INNER so a signal scope /
        // breakdown only counts firing prompts; the namespace predicate(s) ride
        // THIS step's stepFilter (ON ≡ WHERE for an INNER join), co-located like
        // the cluster subtree pred above. SignalTags has no direct endpoint
        // column — endpoint/tenant scoping is carried by the PROMPT root it hangs
        // off, so a signal facet/scope is always tenant- and scope-correct.
        children.push({
            edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "st",
                entity: "ENTITY_TYPE_SIGNAL_TAGS",
                stepFilter: [...signalTagsPreds],
            },
        });
    }
    if (needsAssignment) {
        // PROMPT → ENDPOINT_USER_ASSIGNMENT (junction, on endpoint_id) →
        // DIRECTORY_USER (the person, on the multi-key) (O-2400). INNER so a
        // breakdown/scope only counts attributed prompts; the planner injects
        // each entity's is_deleted=0 row filter + tenant scoping on the ON clause.
        children.push({
            edge: "EDGE_TYPE_PROMPT_HAS_USER_ASSIGNMENTS",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "u",
                entity: "ENTITY_TYPE_ENDPOINT_USER_ASSIGNMENT",
                children: [{
                    edge: "EDGE_TYPE_USER_ASSIGNMENT_HAS_DIRECTORY_USER",
                    mode: "JOIN_MODE_INNER",
                    target: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
                }],
            },
        });
    }
    // The per-prompt usage edge was removed in O-1805, so token / model
    // surfaces don't reach the token store as a child of PROMPT. Anything
    // grouping/summing by model or token roots at ENTITY_TYPE_TOKEN directly
    // (see getOverviewActivityBundle's token pass and the Models-tab entries
    // query in apiClient.js), which carries MODEL + token metrics natively.
    // (O-2021 made EDGE_TYPE_PROMPT_HAS_TOKENS the single generic prompt→tokens
    // edge — a grain-aware collapse attributing each session at its finest grain
    // — but these surfaces keep the TOKEN-rooted shape.) `needsUsage`/`usagePreds` are
    // kept inert here so no token join
    // is ever emitted; the `pu` step is not attached.
    void needsUsage;
    if (children.length) root.children = children;

    // clusterPreds now ride in the ec step's stepFilter (see above), co-located
    // with the generation pin — not the top-level filter — so they're excluded
    // here. directPreds (endpoint/session/flag/model/…) keep their top-level
    // WHERE placement; a `model` scope is now a PROMPT.model predicate in
    // directPreds (see the viaUsage branch above), not a dropped pu-anchored
    // one. usagePreds stays inert — this builder never joins the TOKEN
    // entity off PROMPT (see above), so nothing populates it.
    void usagePreds;
    const allPreds = [...directPreds];
    let filter = null;
    if (allPreds.length === 1) filter = allPreds[0];
    else if (allPreds.length > 1) filter = { andExpr: { children: allPreds } };

    // Fold the steer into the same root + filter the scope produced, so it
    // behaves exactly like an additional scope predicate everywhere downstream.
    if (steer?.filter) {
        const steered = ensureQueryJoins(root, steer.joins || []);
        return { root: steered, filter: andFilterExpr(filter, steer.filter), unsupported };
    }
    // `unsupported` lists scopes this PROMPT root can't carry — empty today
    // (model now rides PROMPT.model). Kept so a caller still fails closed if a
    // future PROMPT-unexpressible scope dim ever lands here.
    return { root, filter, unsupported };
}

// Existence probe for the chat's navigation tool (O-2084): does this
// dashboard scope carry prompt activity *in the given time range*? Keeps
// the agent from navigating to a dead view built on hallucinated/stale
// ids or on a scope that's simply empty in the window the user is looking
// at. We probe the SAME range the landed tab will inherit (the caller
// passes the resolved `timeRange`) — checking a wider window would
// green-light a navigation that lands empty in the active range, which is
// exactly what the gate exists to stop.
//
// Reuses buildPromptRootAndFilter so every dim quirk (synthetic / cluster
// / model) resolves exactly as the real surfaces resolve it. Returns a
// tri-state so the caller can FAIL CLOSED (the gate's whole point is to
// prevent dead navigations — silently landing on an empty view is worse
// than refusing):
//   "active"  — has data, navigate.
//   "empty"   — definitively no data at this scope/range, block.
//   "unknown" — couldn't determine (unsupported dim or probe error), block.
export async function scopeActivityStatus(scopePath = [], timeRange = null) {
    try {
        const { apiScope, unsupported } = dashboardScopeToApiScope(scopePath);
        if (unsupported.length) return "unknown";
        const { root, filter } = buildPromptRootAndFilter({ scopePath: apiScope });
        const spec = {
            root,
            timeRange: timeRange || { relative: { durationSeconds: DEFAULT_DURATION_SECONDS } },
            output: {
                metrics: [{
                    outputAlias: "n",
                    computed: {
                        field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
                        op: "AGGREGATE_OP_COUNT",
                    },
                }],
            },
        };
        if (filter) spec.filter = filter;
        const [row = {}] = await runQueryRows(spec);
        return (Number(row.n) || 0) > 0 ? "active" : "empty";
    } catch {
        return "unknown";
    }
}

// ── Backend signal_tags (durable, server-computed signals) ─────────
//
// The dashboard's signals were historically (re)computed in-browser by running
// kernels over a ~120-prompt window (triggerHarness). The backend now persists
// per-event signal output to `signal_tags` (the SignalTags entity), so a signal
// the backend actually runs can be READ instead of re-derived. Both queries root
// at PROMPT and INNER-join SignalTags over `PromptHasSignalTags` (event_id), so
// tenant + endpoint RBAC scoping rides PROMPT's own scoping — SignalTags has no
// direct endpoint column and is scoped transitively. See docs/specs/signals-contract.md.

const SIGNAL_TAGS_VALUE = "DIMENSION_FIELD_SIGNAL_TAGS_VALUE";
const SIGNAL_TAGS_VALUE_HASH = "DIMENSION_FIELD_SIGNAL_TAGS_VALUE_HASH";
const SIGNAL_BODY_BATCH = 1000;
const NORMALIZED_SIGNAL_NAMESPACES = new Set(["mcp_server", "skill", "tool"]);

async function signalValueHashSupported() {
    try {
        const schema = await describeSchema();
        return (schema?.entities || []).some((entity) => (
            (entity?.fields || []).some((field) => field?.field === SIGNAL_TAGS_VALUE_HASH)
        ));
    } catch {
        return false;
    }
}

export async function runSignalValueRows(spec, run, namespaces = []) {
    const dimensions = spec.output?.dimensions || [];
    const valueDim = dimensions.find((d) => d.field?.field === SIGNAL_TAGS_VALUE);
    const normalizedOnly = namespaces.length > 0 && namespaces.every((ns) => NORMALIZED_SIGNAL_NAMESPACES.has(ns));
    if (!valueDim || !normalizedOnly || spec.output?.metrics?.length || !(await signalValueHashSupported())) return run(spec);
    const alias = valueDim.outputAlias;
    const hashField = { stepAlias: valueDim.field.stepAlias, field: SIGNAL_TAGS_VALUE_HASH };
    const rows = await run({
        ...spec,
        output: { ...spec.output, dimensions: dimensions.map((d) => (d === valueDim ? { field: hashField, outputAlias: alias } : d)) },
    });
    const hashes = [...new Set(rows.map((row) => row[alias]).filter(Boolean))];
    const batches = Array.from({ length: Math.ceil(hashes.length / SIGNAL_BODY_BATCH) }, (_, i) => hashes.slice(i * SIGNAL_BODY_BATCH, (i + 1) * SIGNAL_BODY_BATCH));
    const bodies = new Map();
    for (const batch of batches) {
        const hashPred = { pred: { field: hashField, op: "OPERATOR_IN", value: batch } };
        const found = await run({
            root: spec.root,
            timeRange: spec.timeRange,
            filter: spec.filter ? { andExpr: { children: [spec.filter, hashPred] } } : hashPred,
            output: {
                dimensions: [
                    { field: hashField, outputAlias: "value_hash" },
                    { field: valueDim.field, outputAlias: "value" },
                ],
                metrics: [{ outputAlias: "n", computed: { field: hashField, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
            },
            limit: batch.length,
        });
        for (const row of found) bodies.set(row.value_hash, row.value);
    }
    return rows.map((row) => ({ ...row, [alias]: bodies.get(row[alias]) ?? "" }));
}

const SIGNAL_TAGS_CHILD = {
    edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
    mode: "JOIN_MODE_INNER",
    target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
};

// One row per emitted tag for the given prompt event_ids: { event_id, namespace,
// tag, value, subject_type, evidence? }. Evidence is projected only when
// `includeEvidence` is true and DescribeSchema advertises the field; versioned
// JSON is decoded at this boundary. Returns [] for an empty id list (no query). The
// caller groups by event_id and maps each namespace onto the matching dashboard
// signal. event_id IN (window) is the scope; `range` prunes partitions.
// `namespaces` (optional) restricts to those tag namespaces server-side — pass
// the alert set when badging so the query skips the heavy functional `value`
// blobs (e.g. the full mcp_server tool-surface JSON) the caller would discard.
export async function getSignalTags(eventIds, range = null, { namespaces = null, includeEvidence = false, requestContext = null, signal = null } = {}) {
    const ids = Array.isArray(eventIds) ? [...new Set(eventIds.map(String).filter(Boolean))] : [];
    if (!ids.length) return [];
    let evidenceSupported = false;
    if (includeEvidence) {
        try {
            const schema = await describeSchema();
            evidenceSupported = (schema?.entities || []).some((entity) => (
                (entity?.fields || []).some((field) => field?.field === "DIMENSION_FIELD_SIGNAL_TAGS_EVIDENCE")
            ));
        } catch (error) {
            if (requestContext !== null) throw error;
            evidenceSupported = false;
        }
    }
    const idPred = {
        pred: {
            field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
            op: "OPERATOR_IN",
            value: ids,
        },
    };
    const ns = Array.isArray(namespaces) ? namespaces.filter(Boolean) : [];
    const nsPred = ns.length
        ? { pred: { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" }, op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: ns.length > 1 ? ns : ns[0] } }
        : null;
    const spec = {
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT", children: [SIGNAL_TAGS_CHILD] },
        timeRange: rangeToTimeRange(range),
        filter: nsPred ? { andExpr: { children: [idPred, nsPred] } } : idPred,
        output: {
            dimensions: [
                { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "event_id" },
                { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" }, outputAlias: "namespace" },
                { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" }, outputAlias: "tag" },
                { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_VALUE" }, outputAlias: "value" },
                { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_TYPE" }, outputAlias: "subject_type" },
                ...(evidenceSupported ? [{ field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVIDENCE" }, outputAlias: "evidence" }] : []),
            ],
        },
        limit: 100000,
    };
    const runOpts = requestContext === null ? {} : { cache: false, requestContext, signal };
    const rows = await runSignalValueRows(spec, (s) => runQueryRows(s, runOpts), ns);
    return rows.map((row) => {
        if (includeEvidence && !evidenceSupported) {
            return { ...row, evidence: null, evidence_state: "unavailable" };
        }
        if (!includeEvidence) return row;
        let evidence = null;
        let evidenceState = "invalid";
        if (row.evidence === "") {
            evidenceState = "legacy";
        } else {
            try {
                const parsed = typeof row.evidence === "string" ? JSON.parse(row.evidence) : row.evidence;
                if (parsed?.version === 0) evidenceState = "legacy";
                if (parsed?.version === 1 && Array.isArray(parsed.items)) {
                    evidence = parsed;
                    evidenceState = parsed.evidence_truncated || Number(parsed.omitted_evidence_count) > 0
                        ? "partial"
                        : "complete";
                }
            } catch { /* invalid evidence fails closed at the API boundary */ }
        }
        return { ...row, evidence, evidence_state: evidenceState };
    });
}

// Which signal namespaces the backend is actually producing (recently). Drives
// per-signal routing: a dashboard trigger whose emitted namespace appears here is
// served from durable signal_tags, the rest fall back to the in-browser scan. A
// child-namespace facet (e.g. `skill.fingerprint`) appears too; the verdict mapper
// matches namespaces to triggers exactly, so facets don't masquerade as signals.
// Fail-closed to an empty set so any error leaves the FE on the pre-cutover path.
export async function getActiveSignalNamespaces(range = null) {
    try {
        const spec = {
            root: { alias: "p", entity: "ENTITY_TYPE_PROMPT", children: [SIGNAL_TAGS_CHILD] },
            timeRange: rangeToTimeRange(range),
            output: {
                dimensions: [
                    { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" }, outputAlias: "namespace" },
                ],
                metrics: [
                    { outputAlias: "n", computed: { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" }, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                ],
            },
            limit: 1000,
        };
        const rows = await runQueryRows(spec);
        return new Set(rows.map((r) => r.namespace).filter(Boolean));
    } catch {
        return new Set();
    }
}

// ── TOKEN-rooted scope builder (token / model surfaces) ─────
//
// Token and per-model token surfaces root at ENTITY_TYPE_TOKEN (the
// multi-grain `token_observation` store, O-2021; row-per-observation, with
// finest-grain-per-session-bucket resolution applied server-side) — the
// per-prompt usage edge was removed in O-1805, so they don't hang off a
// PROMPT root. This re-anchors the supported scope predicates onto TOKEN's
// own columns:
//
//   endpoint → DIMENSION_FIELD_TOKEN_ENDPOINT_ID
//   session  → DIMENSION_FIELD_TOKEN_SESSION_ID
//   agent    → DIMENSION_FIELD_TOKEN_PROVIDER (canonical provider tag)
//
// Scopes with no TOKEN column (cluster / flag / tool_call / mcp_server /
// skill) are returned in `unsupported` so the caller can fail closed
// (render an empty token layer) rather than silently under-scope.
// Returns { root, filter, unsupported }; the root alias is `pu`.
const USAGE_SCOPE_FIELD = {
    endpoint: "DIMENSION_FIELD_TOKEN_ENDPOINT_ID",
    session:  "DIMENSION_FIELD_TOKEN_SESSION_ID",
    agent:    "DIMENSION_FIELD_TOKEN_PROVIDER",
    // model lives natively on TOKEN, so a model scope is a direct column
    // filter here — no collapse needed.
    model:    "DIMENSION_FIELD_TOKEN_MODEL",
};

// The TOKEN column a dim's values live on — for GROUPING/ranking on the TOKEN
// root, not just scope-filtering. Single source of truth shared by
// buildUsageRootAndFilter (scope filter) and the TOKEN-rooted dim ranker
// (executeUsageDimEntries' "Most tokens" path). null for dims with no TOKEN
// column (cluster / flag / tool_call / mcp_server / skill / file / ...), which
// therefore cannot be token-sorted.
export function usageScopeFieldFor(dim) {
    return USAGE_SCOPE_FIELD[dim] || null;
}
export function buildUsageRootAndFilter({ scopePath = [] } = {}) {
    const root = { alias: "pu", entity: "ENTITY_TYPE_TOKEN" };
    const preds = [];
    const unsupported = [];
    for (const step of scopePath) {
        const ids = step.ids?.length ? step.ids : step.id != null ? [step.id] : [];
        if (!ids.length) continue;
        const field = USAGE_SCOPE_FIELD[step.dim];
        if (!field) { unsupported.push(step.dim); continue; }
        // All TOKEN-expressible scopes are string-valued on the wire
        // (UUID endpoint/session ids carried base64; provider tag is a string).
        const values = ids.map(String);
        preds.push({
            pred: {
                field: { stepAlias: "pu", field },
                op: values.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
                value: values.length > 1 ? values : values[0],
            },
        });
    }
    let filter = null;
    if (preds.length === 1) filter = preds[0];
    else if (preds.length > 1) filter = { andExpr: { children: preds } };
    return { root, filter, unsupported };
}

// ── Collapse-rooted token/model builder (prompt-analysis scopes) ───────
//
// For scopes TOKEN can't express directly (cluster / tool_call / file /
// flag / mcp_server / skill — anything that lives on the PROMPT analysis row),
// build the PROMPT_HAS_TOKENS collapse: a filter-only PROMPT root (scope
// as its `stepFilter`, plus an ILO_EVENT_CLUSTER filter-join for cluster scope)
// → TOKEN via EDGE_TYPE_PROMPT_HAS_TOKENS, the single generic prompt→tokens
// edge — grain-resolved tokens for the prompts matching the filter. The planner
// absorbs the whole PROMPT(+cluster) source into a tenant/time-scoped semi-join
// on the token scan, emitting a grain-gated OR over the three attribution keys
// (`(grain='turn' AND unit_id IN (SELECT event_id …)) OR (grain='submission'
// AND submission_id IN (…)) OR (grain='session' AND session_id IN (…))`, each
// subselect over `ai_prompt_analysis [JOIN ilo_event_cluster]`), so per-model
// token aggregates count each
// observation once — deduped across capture sources/grains, no fan-out, no
// 1024-`IN` cap (the subtree walk is server-side), and the generation pin is
// injected by runQuery. The caller puts its model dimension + token metrics on
// the returned `pu` step.
//
// CONTRACT (enforced by the planner's collapsing-edge validation): scope
// predicates ride PROMPT's `stepFilter` (NOT a top-level filter — that's
// rejected on the collapse source); only `pu` carries output. A scope with no
// PROMPT mapping (today: model-as-scope, handled by buildUsageRootAndFilter)
// comes back in `unsupported` so the caller fails closed.
//
// Returns { root, puAlias: "pu", unsupported }.
export function buildUsageCollapse({ scopePath = [] } = {}) {
    const promptPreds = []; // PROMPT-direct + synthetic → PROMPT stepFilter
    const clusterPreds = []; // cluster_subtree → ec stepFilter
    const signalTagsPreds = []; // st.namespace → st stepFilter
    let needsCluster = false;
    let needsSignalTags = false;
    const unsupported = [];

    for (const step of scopePath) {
        const info = DIM_INFO[step.dim];
        if (!info) { unsupported.push(step.dim); continue; }
        const ids = step.ids?.length ? step.ids : step.id != null ? [step.id] : [];
        if (!ids.length) continue;
        if (info.synthetic) {
            const combined = getDimAdapter(step.dim)?.predicate(ids, info.onStep) ?? null;
            if (combined) promptPreds.push(combined);
            continue;
        }
        if (info.viaCluster) {
            needsCluster = true;
            clusterPreds.push(predicate(info.onStep, info.scopeField ?? info.field, ids, step.dim));
        } else if (info.viaSignalTags) {
            // Signal scope rides the collapse source via a SignalTags INNER join
            // (dim ids are commands → backend namespaces). The collapse folds the
            // PROMPT source into a semi-join (event_id IN (… JOIN signal_tags …)),
            // so the 1-to-many PROMPT→SignalTags fan-out is deduped by the IN — a
            // prompt with N tags still contributes its tokens once. Verified the
            // planner accepts this non-cluster join on the collapse source.
            const ns = ids.map(namespaceForCommand).filter(Boolean);
            if (ns.length) {
                needsSignalTags = true;
                signalTagsPreds.push({
                    pred: {
                        field: { stepAlias: info.onStep, field: info.field },
                        op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
                        value: ns.length > 1 ? ns : ns[0],
                    },
                });
            }
        } else if (info.viaUsage) {
            // model-as-scope is a direct TOKEN filter — buildUsageRootAndFilter
            // owns it; a `pu`-anchored predicate has no place on the PROMPT source.
            unsupported.push(step.dim);
        } else if (info.viaAssignment) {
            // Identity scope can't ride the TOKEN-collapse source — the `u`
            // join would fan the collapse and the collapse-edge validator
            // rejects a non-PROMPT join on the source. Fail closed so a
            // model/token breakdown under an identity scope blanks rather than
            // mis-attributing (O-2400); same posture as model-as-scope above.
            unsupported.push(step.dim);
        } else {
            // endpoint/session/agent + tool_call/file → PROMPT's own columns.
            promptPreds.push(predicate(info.onStep, info.field, ids, step.dim));
        }
    }

    const children = [];
    if (needsCluster) {
        children.push({
            edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "ec",
                entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
                // Outlier exclusion + cluster_subtree scope, co-located with the
                // generation pin runQuery injects (same as buildPromptRootAndFilter).
                stepFilter: [
                    { pred: { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, op: "OPERATOR_GT", value: 0 } },
                    ...clusterPreds,
                ],
            },
        });
    }
    if (needsSignalTags) {
        // PROMPT → SignalTags semi-join on the collapse source — scopes the token
        // collapse to the signal's firing prompts. Co-located with the ec join above.
        children.push({
            edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
            mode: "JOIN_MODE_INNER",
            target: {
                alias: "st",
                entity: "ENTITY_TYPE_SIGNAL_TAGS",
                stepFilter: [...signalTagsPreds],
            },
        });
    }
    // The collapse target — TOKEN via the collapsing edge. The caller
    // hangs its model dimension + token metrics off this `pu` step.
    children.push({
        edge: "EDGE_TYPE_PROMPT_HAS_TOKENS",
        mode: "JOIN_MODE_INNER",
        target: { alias: "pu", entity: "ENTITY_TYPE_TOKEN" },
    });

    const root = { alias: "p", entity: "ENTITY_TYPE_PROMPT", children };
    if (promptPreds.length) root.stepFilter = promptPreds;
    return { root, puAlias: "pu", unsupported };
}

// Small helper: build a single Predicate FilterExpr. For scalar dims
// it picks EQ vs IN by id-list length. For Array(String) dims (file,
// tool_call) the proto restricts ops to LIKE/ILIKE/IN/NOT_IN with
// element-wise semantics — EQ isn't valid — so we always emit IN.
function predicate(stepAlias, field, ids, dim) {
    const cast = dim === "cluster" ? Number : String;
    const values = ids.map(cast);
    const isArray = DIM_INFO[dim]?.array;
    if (isArray) {
        return {
            pred: { field: { stepAlias, field }, op: "OPERATOR_IN", value: values },
        };
    }
    return {
        pred: {
            field: { stepAlias, field },
            op: values.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
            value: values.length > 1 ? values : values[0],
        },
    };
}

// ── Bucket densification ───────────────────────────────────────────

// Sparse rows from the analytics API → fixed-length per-id arrays.
//   rows:        [{ id: dim_value, bucket: ms, value: number }, ...]
//   bucketCount: target array length
//   fromMs, toMs, bucketSeconds: from rangeBoundsMs + bucketSecondsForRange
//
// Buckets are aligned to the planner's `toStartOfInterval` semantics:
//   bucketIdx = floor((bucket_ms - fromMs) / 1000 / bucketSeconds)
// clamped to [0, bucketCount-1].
export function densifyBuckets(rows, { fromMs, toMs, bucketCount, bucketSeconds }) {
    const out = new Map();
    const stepMs = Math.max(1, bucketSeconds * 1000);
    for (const r of rows) {
        const id = String(r.id);
        let arr = out.get(id);
        if (!arr) {
            arr = new Array(bucketCount).fill(0);
            out.set(id, arr);
        }
        // parseServerTimestamp, not Date.parse: ClickHouse bucket starts
        // arrive as `"YYYY-MM-DD HH:MM:SS"` with no zone marker. Date.parse
        // reads those as *local*, so the bucket lands at an offset-shifted
        // index while the grid origin (fromMs, from an ISO-with-Z range) is
        // true-epoch — the data renders in the wrong time-of-day. (numbers
        // pass straight through.)
        const bMs = parseServerTimestamp(r.bucket);
        if (!Number.isFinite(bMs)) continue;
        const idx = Math.max(0, Math.min(bucketCount - 1,
            Math.floor((bMs - fromMs) / stepMs)));
        arr[idx] += Number(r.value) || 0;
    }
    return out; // Map<id, number[]>
}
